import { useDisclosure } from '@shared/hooks';
import { useCallback, useRef, useState } from 'react';

import { Box } from '../box';
import { styled } from '../theme';
import { Tooltip } from '../tooltip';
import {
  mergeSx,
  type SxPropsWithTheme,
  type OverridableComponent,
  type OverridableTypeMap,
} from '../utils';

import type {
  CopyToClipboardSlotProps,
  CopyToClipboardAction,
  CopyToClipboardProps,
  StyledComponentProps,
} from './types';
import { getBaseCopyToClipboardStyles, isOverridableComponent } from './utils';

const StyledComponent = styled(
  ({ as: Component = 'span', ...props }: StyledComponentProps) => <Component {...props} />,
  {
    shouldForwardProp: (prop) => prop !== 'as',
  },
)(({ theme }) => getBaseCopyToClipboardStyles(theme));

/**
 * CopyToClipboard component
 *
 * A versatile component for copying text to the clipboard.
 * It supports both MUI components and generic JSX components through the `as` prop.
 * Additionally, it accepts all props of the specified component along with `text` and `onCopy` props.
 *
 * @example
 * // With MUI component
 * <CopyToClipboard text="158-0-1-1-2-X-1D-3-4-1-1" as={Chip} label="158-0-1-1-2-X-1D-3-4-1-1" />
 *
 *
 * @example
 * // With JSX component
 * <CopyToClipboard text="158-0-1-1-2-X-1D-3-4-1-1" as="span">
 *  158-0-1-1-2-X-1D-3-4-1-1
 * </CopyToClipboard>
 *
 */
export default function CopyToClipboard<
  TTypeMap extends OverridableTypeMap,
  T extends OverridableComponent<TTypeMap> | keyof JSX.IntrinsicElements = typeof Box,
>(props: Readonly<CopyToClipboardProps<TTypeMap, T>>) {
  const { onCopy, text, slotProps, as, ...restProps } = props;
  const { tooltip } = (slotProps ?? {}) as CopyToClipboardSlotProps;
  const {
    duration = 2000,
    placement = 'right',
    hoverText = 'Click to Copy',
    successText = 'Copied',
  } = tooltip ?? {};

  const [isTooltipOpen, tooltipOpenHandlers] = useDisclosure(false);
  const { open: openTooltip, close: closeTooltip } = tooltipOpenHandlers;
  const [action, setAction] = useState<CopyToClipboardAction | undefined>(undefined);
  const timeOutRef = useRef<NodeJS.Timeout>();

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      try {
        event.stopPropagation();
        event.preventDefault();
        setAction('click');
        openTooltip();
        await navigator.clipboard.writeText(text);
        onCopy?.();
      } catch (error) {
        console.error('Failed to copy to clipboard', { error });
      } finally {
        clearTimeout(timeOutRef.current);
        timeOutRef.current = setTimeout(() => {
          closeTooltip();
          setAction(undefined);
        }, duration);
      }
    },
    [text, duration, openTooltip, closeTooltip, onCopy],
  );

  const handleMouseEnter = useCallback(() => {
    setAction('hover');
    openTooltip();
  }, [openTooltip]);

  const handleMouseLeave = useCallback(() => {
    closeTooltip();
  }, [closeTooltip]);

  // Check if the component is MUI component or not
  // If it is not a MUI component, we'll render the styled component
  if (as && !isOverridableComponent(as)) {
    return (
      <Tooltip
        arrow={false}
        open={isTooltipOpen}
        disableHoverListener
        disableFocusListener
        title={action === 'hover' ? hoverText : successText}
        placement={placement}
      >
        <StyledComponent
          as={as}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...restProps}
        />
      </Tooltip>
    );
  }

  const Component: React.ElementType = as ?? Box;
  const sx = 'sx' in restProps ? (restProps.sx as SxPropsWithTheme) : undefined;

  return (
    <Tooltip
      arrow={false}
      open={isTooltipOpen}
      disableHoverListener
      disableFocusListener
      title={action === 'hover' ? hoverText : successText}
      placement={placement}
    >
      <Component
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...restProps}
        sx={mergeSx(getBaseCopyToClipboardStyles, sx)}
      />
    </Tooltip>
  );
}
